/**
 * Learn — Trinetra platform documentation surface.
 *
 * Scope: how the platform itself works (scoring, portfolio
 * organisation, agent runs, BYOK, etc.). General security concepts
 * live separately at https://learning.nirvik.ai — this page does NOT
 * try to be a security curriculum.
 */
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, ShieldCheck, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

interface PlatformGuide {
  href: string;
  eyebrow: string;
  title: string;
  description: string;
}

// Curated platform guides. Add a new entry here and create a matching
// /my/learn/<slug> route to publish a new guide. Kept short on
// purpose — this surface is meant to grow gradually with real-user
// questions, not be padded with placeholders.
const PLATFORM_GUIDES: PlatformGuide[] = [
  {
    href: '/my/learn/scoring',
    eyebrow: 'Scoring',
    title: 'How security scores work',
    description:
      'The penalty model, why portfolio + org scores are not averages, and how to organise projects so the numbers stay meaningful.',
  },
  {
    href: '/my/learn/quantum-readiness',
    eyebrow: 'Quantum Readiness',
    title: 'Post-quantum readiness — what Trinetra evaluates today',
    description:
      'The 0–4 PQ maturity scale, what gets inspected on endpoints and repos, and how the findings map to NIST PQC + NSA CNSA 2.0.',
  },
  {
    href: '/my/learn/findings-lifecycle',
    eyebrow: 'Lifecycle',
    title: 'The lifecycle of a finding',
    description:
      'How a finding moves from open → fix-merged → fixed, and how SARIF partial fingerprints stop re-runs from creating duplicate rows or duplicate PRs.',
  },
];

export function LearnPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Learn Trinetra</h1>
        <p className="text-muted-foreground mt-1 max-w-2xl">
          Short guides on how the platform works — scoring, portfolio shape, agents, integrations, and more.
          Looking for general security concepts? Head to{' '}
          <a
            href="https://learning.nirvik.ai"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary-strong hover:underline"
          >
            learning.nirvik.ai <ExternalLink className="h-3 w-3" />
          </a>{' '}
          — our standalone learning platform.
        </p>
      </div>

      {/* Platform guides */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Platform guides
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PLATFORM_GUIDES.map((g) => (
            <Link key={g.href} to={g.href} className="block">
              <Card className="h-full hover:border-primary/40 transition-colors">
                <CardContent className="flex flex-col gap-3 p-5">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                      <ShieldCheck className="h-5 w-5 text-primary-strong" />
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-saffron">{g.eyebrow}</p>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-foreground leading-snug">{g.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1.5">{g.description}</p>
                  </div>
                  <div className="pt-2 border-t border-border">
                    <span className="text-sm text-primary-strong inline-flex items-center gap-1">
                      Read guide <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Coming-soon placeholder. Honest "we're building" rather than
          fake topic cards. Removed when there's a meaningful second
          guide ready to ship. */}
      <Card className="p-5 border-dashed">
        <p className="text-sm font-semibold text-foreground">More guides on the way</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          We are working on guides for connecting repos, BYOK provider setup, agent run anatomy, compliance posture,
          and reading audit logs. Have a question that should be a guide? Open an issue or ping us.
        </p>
      </Card>
    </div>
  );
}
